import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import "@/App.css";
import { AuthProvider, useAuth } from "@/lib/auth";
import { FarmProvider } from "@/lib/farmContext";
import { Toaster } from "@/components/ui/sonner";
import { Header } from "@/components/Header";
import { BottomNav } from "@/components/BottomNav";
import { OfflineBanner } from "@/components/OfflineBanner";
import { PendingSyncBanner } from "@/components/PendingSyncBanner";
import { SessionExpiredBanner } from "@/components/SessionExpiredBanner";
import { MergedSyncNotice } from "@/components/MergedSyncNotice";
import { SystemResetNotice } from "@/components/SystemResetNotice";
import { UpdateAvailableBanner } from "@/components/UpdateAvailableBanner";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { startAutoSync, onSyncState } from "@/lib/sync";
import { db } from "@/lib/db";
import { initTheme } from "@/lib/theme";
import { LocalBackupScheduler } from "@/components/LocalBackupScheduler";

// Pages
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AcceptInvite from "@/pages/AcceptInvite";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Animals from "@/pages/Animals";
import NewAnimal from "@/pages/NewAnimal";
import InitialInventory from "@/pages/InitialInventory";
import RegisterBirth from "@/pages/RegisterBirth";
import Actions from "@/pages/Actions";
import ActionRunner from "@/pages/ActionRunner";
import AnimalDetail from "@/pages/AnimalDetail";
import EditAnimal from "@/pages/EditAnimal";
import AIReport from "@/pages/AIReport";
import Inventory from "@/pages/Inventory";
import NewInventory from "@/pages/NewInventory";
import InventorySession from "@/pages/InventorySession";
import InventoryResult from "@/pages/InventoryResult";
import Search from "@/pages/Search";
import Farms from "@/pages/Farms";
import FarmDetail from "@/pages/FarmDetail";
import Settings from "@/pages/Settings";
import ProfileSettings from "@/pages/ProfileSettings";
import ReaderSettings from "@/pages/ReaderSettings";
import UsersSettings from "@/pages/UsersSettings";
import ExportSettings from "@/pages/ExportSettings";
import BackupsSettings from "@/pages/BackupsSettings";
import SyncSettings from "@/pages/SyncSettings";
import AuditLog from "@/pages/AuditLog";
import Trash from "@/pages/Trash";
import About from "@/pages/About";
import PredictedBirths from "@/pages/PredictedBirths";
import DispatchesReport from "@/pages/DispatchesReport";

// Inicializar tema antes de montar React (evita FOUC light/dark)
initTheme();

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground">Cargando…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// El veterinario tiene acceso acotado (registrar palpaciones, buscar/ver
// animales de su finca, nacimientos previstos). Cualquier otra ruta lo
// redirige de vuelta a su pantalla de trabajo en vez de mostrar un 403 en blanco.
function VetBlock({ children }) {
  const { user } = useAuth();
  if (user?.role === "vet") return <Navigate to="/acciones/11" replace />;
  return children;
}

// Fix P (2026-10-06): pedido del dueño — los encargados no usan Acciones
// (registros, ventas, muertes, nacimientos).
function HandlerBlock({ children }) {
  const { user } = useAuth();
  if (user?.role === "handler") return <Navigate to="/animales" replace />;
  return children;
}

function Home() {
  const { user } = useAuth();
  if (user?.role === "vet") return <Navigate to="/acciones/11" replace />;
  return <Dashboard />;
}

function AppShell({ children }) {
  const location = useLocation();
  const noShell = ["/onboarding"].some((p) => location.pathname.startsWith(p));
  if (noShell) return <>{children}</>;
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <OfflineBanner />
      <SessionExpiredBanner />
      <PendingSyncBanner />
      <MergedSyncNotice />
      <SystemResetNotice />
      <UpdateAvailableBanner />
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-5 pb-28">
        {children}
      </main>
      <BottomNav />
      <div className="h-16" />
    </div>
  );
}

function AutoSyncStarter() {
  const { user } = useAuth();
  useEffect(() => {
    if (!user) return;
    const cleanup = startAutoSync(30000);
    return cleanup;
  }, [user]);
  return null;
}

// Fix 4 (hotfix pérdida de datos 31/07/2026): tras un login en un dispositivo
// sin datos locales (recién instalado, o luego de un wipe por cambio de
// dueño), nunca se muestra el dashboard en cero — eso es justo lo que el
// cliente interpretó como "se borró todo". Se espera el primer pull completo.
function InitialSyncGate({ children }) {
  const counts = useLiveQuery(async () => ({
    animals: await db.animals.count(),
    farms: await db.farms.count(),
  }), []);
  const [firstSyncTerminal, setFirstSyncTerminal] = useState(false);

  useEffect(() => {
    const off = onSyncState((s) => {
      if (["idle", "error", "offline", "auth_expired"].includes(s.phase)) setFirstSyncTerminal(true);
    });
    // Salvaguarda: si por algún motivo el motor de sync nunca reporta un
    // estado final, no dejamos al usuario trabado en la pantalla de carga.
    const timeout = setTimeout(() => setFirstSyncTerminal(true), 45000);
    return () => { off(); clearTimeout(timeout); };
  }, []);

  const stillChecking = counts === undefined;
  const localEmpty = !stillChecking && counts.animals === 0 && counts.farms === 0;
  const showGate = localEmpty && !firstSyncTerminal;

  if (showGate) {
    return (
      <div className="min-h-screen grid place-items-center bg-background px-6" data-testid="initial-sync-gate">
        <div className="text-center max-w-xs">
          <div className="mx-auto mb-4 h-10 w-10 rounded-full border-2 border-primary border-t-transparent animate-spin" />
          <p className="font-heading font-semibold text-foreground">Descargando tus datos del servidor…</p>
          <p className="text-xs text-muted-foreground mt-1.5">No cierres la app, esto puede tardar unos segundos.</p>
        </div>
      </div>
    );
  }
  return children;
}

function Protected() {
  return (
    <RequireAuth>
      <FarmProvider>
      <AutoSyncStarter />
      <LocalBackupScheduler />
      <InitialSyncGate>
        <AppShell>
          <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/acciones" element={<HandlerBlock><Actions /></HandlerBlock>} />
          <Route path="/acciones/:id" element={<HandlerBlock><ActionRunner /></HandlerBlock>} />
          <Route path="/animales" element={<Animals />} />
          <Route path="/animales/nuevo" element={<VetBlock><NewAnimal /></VetBlock>} />
          <Route path="/inventario-inicial" element={<VetBlock><InitialInventory /></VetBlock>} />
          <Route path="/animales/nacimiento" element={<VetBlock><HandlerBlock><RegisterBirth /></HandlerBlock></VetBlock>} />
          <Route path="/animales/:id" element={<AnimalDetail />} />
          <Route path="/animales/:id/editar" element={<VetBlock><EditAnimal /></VetBlock>} />
          <Route path="/animales/:id/ia" element={<VetBlock><AIReport /></VetBlock>} />
          <Route path="/inventario" element={<VetBlock><Inventory /></VetBlock>} />
          <Route path="/inventario/nuevo" element={<VetBlock><NewInventory /></VetBlock>} />
          <Route path="/inventario/:id" element={<VetBlock><InventoryResult /></VetBlock>} />
          <Route path="/inventario/:id/sesion" element={<VetBlock><InventorySession /></VetBlock>} />
          <Route path="/buscar" element={<Search />} />
          <Route path="/nacimientos-previstos" element={<PredictedBirths />} />
          <Route path="/despachos" element={<VetBlock><DispatchesReport /></VetBlock>} />
          <Route path="/fincas" element={<VetBlock><Farms /></VetBlock>} />
          <Route path="/fincas/:id" element={<VetBlock><FarmDetail /></VetBlock>} />
          <Route path="/ajustes" element={<Settings />} />
          <Route path="/ajustes/perfil" element={<ProfileSettings />} />
          <Route path="/ajustes/lector" element={<ReaderSettings />} />
          <Route path="/ajustes/usuarios" element={<VetBlock><UsersSettings /></VetBlock>} />
          <Route path="/ajustes/exportar" element={<VetBlock><ExportSettings /></VetBlock>} />
          <Route path="/ajustes/backups" element={<VetBlock><BackupsSettings /></VetBlock>} />
          <Route path="/ajustes/papelera" element={<VetBlock><Trash /></VetBlock>} />
          <Route path="/ajustes/sync" element={<SyncSettings />} />
          <Route path="/ajustes/auditoria" element={<VetBlock><AuditLog /></VetBlock>} />
          <Route path="/ajustes/acerca" element={<About />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </AppShell>
      </InitialSyncGate>
      </FarmProvider>
    </RequireAuth>
  );
}

export default function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/registrarse" element={<Register />} />
              <Route path="/invite/:token" element={<AcceptInvite />} />
              <Route path="/onboarding" element={<RequireAuth><Onboarding /></RequireAuth>} />
              <Route path="/*" element={<Protected />} />
            </Routes>
            <Toaster position="top-center" />
          </BrowserRouter>
        </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}
